import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import {
  DEFAULT_FORMAT_SETTINGS, formatDate, formatDateTime, formatDuration, formatMegabytes, formatMoney, formatMonth,
  formatMonthName, formatNumber, formatPhone, formatTime, formatWeekday, formatYear, fromMinorUnits, toMinorUnits, todayIso, type FormatSettings,
} from '../format';
import { storage } from '../storage';
import { isolate } from './isolate';
import { commonStrings } from './strings';
import {
  DEFAULT_LOCALE, DIRECTION, type Catalogue, type Gender, type Locale, type MessageParams, type Messages,
} from './types';

export interface I18nValue {
  locale: Locale;
  direction: 'rtl' | 'ltr';
  setLocale: (locale: Locale) => void;

  /** Looks up a message and fills its `{placeholders}`. */
  t: (key: string, params?: MessageParams) => string;
  /** Looks up the plural form for `count` and fills `{count}` (NFR-I18N-04). */
  tn: (key: string, count: number, params?: MessageParams) => string;
  /** Looks up the form matching a child's gender, falling back to the neutral key. */
  tg: (key: string, gender: Gender | null | undefined, params?: MessageParams) => string;
  /** True when a key exists in the active catalogue — for optional labels, not for control flow. */
  has: (key: string) => boolean;

  settings: FormatSettings;
  /** Applies the tenant's timezone, currency and numeral preference once it is known. */
  applySettings: (settings: Partial<FormatSettings>) => void;

  formatDate: (value: string | Date | null | undefined) => string;
  formatTime: (value: string | Date | null | undefined) => string;
  formatDateTime: (value: string | Date | null | undefined) => string;
  /** A recording's length, `m:ss`. */
  formatDuration: (milliseconds: number) => string;
  formatMonth: (value: string | Date | null | undefined) => string;
  /** A month's name alone, for 1 to 12. */
  formatMonthName: (month: number) => string;
  /** A year in the tenant's digits, with no separator. */
  formatYear: (year: number) => string;
  formatWeekday: (value: string | Date | null | undefined) => string;
  formatNumber: (value: number) => string;
  /** Bytes as megabytes, `2.5`. */
  formatMegabytes: (bytes: number) => string;
  formatMoney: (minorUnits: number, currency?: string) => string;
  /** The inverse of `formatMoney`, for a form that takes whole currency units and must send minor ones. */
  toMinorUnits: (amount: number, currency?: string) => number;
  /** Stored minor units as whole units, to fill an entry form; the inverse of `toMinorUnits`. */
  fromMinorUnits: (minorUnits: number, currency?: string) => number;
  formatPhone: (e164: string | null | undefined) => string;
  today: () => string;
}

const I18nContext = createContext<I18nValue | null>(null);

const LOCALE_KEY = 'daghfal.locale';

export interface I18nProviderProps {
  /** The application's own messages, merged over the shared catalogue. */
  catalogue?: Catalogue;
  children: ReactNode;
}

export function I18nProvider({ catalogue, children }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);
  const [settings, setSettings] = useState<FormatSettings>(() => ({
    ...DEFAULT_FORMAT_SETTINGS,
    locale: readStoredLocale(),
  }));

  const messages: Record<Locale, Messages> = useMemo(() => ({
    ar: { ...commonStrings.ar, ...(catalogue?.ar ?? {}) },
    en: { ...commonStrings.en, ...(catalogue?.en ?? {}) },
  }), [catalogue]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    setSettings((current) => ({ ...current, locale: next }));
    storage.write(LOCALE_KEY, next);
  }, []);

  /**
   * Applies the tenant's formatting preferences once they are known.
   *
   * Two things here are load-bearing. It is a `useCallback` with no dependencies, so its identity never
   * changes — callers put it in an effect's dependency list, and a function that changed whenever the
   * settings did would re-run that effect on its own result. And it returns the *existing* object when
   * nothing differs, so React bails out of the re-render instead of treating a fresh object with
   * identical fields as a change. Either one alone still loops; both together do not.
   */
  const applySettings = useCallback((next: Partial<FormatSettings>) => {
    setSettings((current) => {
      const merged = { ...current, ...next };
      const unchanged = (Object.keys(merged) as (keyof FormatSettings)[])
        .every((key) => merged[key] === current[key]);
      return unchanged ? current : merged;
    });
  }, []);

  // `dir` and `lang` belong on the root element, not on a wrapper div: the direction has to reach the
  // scrollbar, the form controls and anything rendered into a portal (NFR-USE-01).
  useEffect(() => {
    const root = globalThis.document?.documentElement;
    if (!root) return;
    root.lang = locale;
    root.dir = DIRECTION[locale];
  }, [locale]);

  const value = useMemo<I18nValue>(() => {
    const active = messages[locale];
    const fallback = messages[DEFAULT_LOCALE];

    const lookup = (key: string): string | undefined => active[key] ?? fallback[key];
    const direction = DIRECTION[locale];

    const t = (key: string, params?: MessageParams): string => interpolate(lookup(key) ?? key, direction, params);

    const pluralRules = new Intl.PluralRules(locale);

    const tn = (key: string, count: number, params?: MessageParams): string => {
      // `Intl.PluralRules` does not have a `zero` category for Arabic cardinals, but "no children" reads
      // very differently from "0 children", so an explicit zero key wins when the catalogue supplies one.
      const category = count === 0 && lookup(`${key}.zero`) ? 'zero' : pluralRules.select(count);
      const template = lookup(`${key}.${category}`) ?? lookup(`${key}.other`) ?? lookup(key) ?? key;
      return interpolate(template, direction, { count: formatNumber(count, settings), ...params });
    };

    const tg = (key: string, gender: Gender | null | undefined, params?: MessageParams): string => {
      const gendered = gender ? lookup(`${key}.${gender === 'Male' ? 'male' : 'female'}`) : undefined;
      return interpolate(gendered ?? lookup(key) ?? key, direction, params);
    };

    return {
      locale,
      direction,
      setLocale,
      t,
      tn,
      tg,
      has: (key: string) => lookup(key) !== undefined,
      settings,
      applySettings,
      formatDate: (v) => formatDate(v, settings),
      formatTime: (v) => formatTime(v, settings),
      formatDateTime: (v) => formatDateTime(v, settings),
      formatDuration: (ms) => formatDuration(ms, settings),
      formatMonth: (v) => formatMonth(v, settings),
      formatMonthName: (month) => formatMonthName(month, settings),
      formatYear: (year) => formatYear(year, settings),
      formatWeekday: (v) => formatWeekday(v, settings),
      formatNumber: (v) => formatNumber(v, settings),
      formatMegabytes: (bytes) => formatMegabytes(bytes, settings),
      formatMoney: (minorUnits, currency) => formatMoney(minorUnits, settings, currency),
      toMinorUnits: (amount, currency) => toMinorUnits(amount, currency ?? settings.currency),
      fromMinorUnits: (minorUnits, currency) => fromMinorUnits(minorUnits, currency ?? settings.currency),
      formatPhone: (v) => formatPhone(v, settings),
      today: () => todayIso(settings),
    };
  }, [applySettings, locale, messages, setLocale, settings]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n was called outside an <I18nProvider>.');
  return value;
}

/**
 * Fills `{placeholder}` slots.
 *
 * Interpolation is the only string assembly allowed: a translator needs the whole sentence in one message
 * so that word order and agreement are theirs to decide (NFR-I18N-01). An unmatched placeholder is left
 * visible rather than blanked, because a missing value should look wrong in review, not in production.
 */
function interpolate(template: string, direction: 'rtl' | 'ltr', params?: MessageParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? isolate(params[name], direction) : match);
}

function readStoredLocale(): Locale {
  const stored = storage.read(LOCALE_KEY);
  return stored === 'ar' || stored === 'en' ? stored : DEFAULT_LOCALE;
}
